import { useMemo, useState } from "react";
import {
  tieredFinancialFee,
  specializedBodyNotionalFee,
  stampBreakdown,
} from "../utils/calculations";
import { toRial, toDisplay } from "../utils/convert";

export function useLawyerFeeCalculator() {
  const [caseType, setCaseType] = useState("financial");
  const [unit, setUnit] = useState("rial"); // rial | toman

  // مالی
  const [financialBasis, setFinancialBasis] = useState("tariff"); // tariff | agreed
  const [claimAmount, setClaimAmount] = useState("");
  const [agreedFee, setAgreedFee] = useState("");
  const [termination, setTermination] = useState("none");

  // ضرایب مشترک
  const [education, setEducation] = useState("none");
  const [experience, setExperience] = useState(0);
  const [cityTier, setCityTier] = useState("none");

  // کیفری
  const [criminalTier, setCriminalTier] = useState("minor"); // minor | major | other
  const [repeatCounts, setRepeatCounts] = useState(0);

  // نظام صنفی وکیل (فقط برای امور اجرایی کاربرد دارد؛ در دعاوی مالی هر دو نهاد همزمان محاسبه می‌شود)
  const [association, setAssociation] = useState("kanoon"); // kanoon | markaz

  // اجرایی
  const [executionAmount, setExecutionAmount] = useState("");

  // مراجع اختصاصی
  const [specializedAmount, setSpecializedAmount] = useState("");

  const unitLabel = unit === "toman" ? "تومان" : "ریال";

  const modifierFraction = useMemo(() => {
    let f = 0;
    if (education === "masters") f += 0.05;
    if (education === "phd") f += 0.1;
    f += Math.min(Number(experience) || 0, 30) * 0.01;
    if (cityTier === "tehran") f += 0.1;
    if (cityTier === "other") f += 0.05;
    return f;
  }, [education, experience, cityTier]);

  const result = useMemo(() => {
    if (caseType === "financial") {
      let withMods;
      let base;

      if (financialBasis === "agreed") {
        // بر اساس مبلغ حق‌الوکاله مندرج در وکالت‌نامه — مطابق ردیف‌های ۷۸ تا ۸۶ اکسل مرجع
        // اینجا خود مبلغ توافقی مبنای محاسبه است و ضرایب تعرفه (تحصیلات/سابقه/شهر) و پلکان بهای خواسته اعمال نمی‌شود
        base = toRial(agreedFee, unit);
        withMods = base;
      } else {
        const amt = toRial(claimAmount, unit);
        base = tieredFinancialFee(amt);
        withMods = base * (1 + modifierFraction);
      }

      let stageFirst = withMods * 0.6;
      let stageAppeal = withMods * 0.4;

      if (termination === "voidBeforeDefense") {
        stageFirst *= 0.25;
        stageAppeal = 0;
      } else if (termination === "voidAfterDefense") {
        stageFirst *= 0.5;
        stageAppeal = 0;
      } else if (termination === "lapseBeforeDefense") {
        stageAppeal *= 0.25;
      } else if (termination === "lapseAfterDefense") {
        stageAppeal *= 0.5;
      }

      const total = stageFirst + stageAppeal;

      // تمبر مالیاتی — طبق فایل اکسل مرجع (شیت «حق الوکاله»):
      // تمبر پایه هر مرحله = ۵٪ حق‌الوکاله همان مرحله.
      // جمع نهایی تمبر = خودِ تمبر پایه + سهم‌های اضافی نصف/ربع:
      // کانون وکلا: تمبر پایه + (تمبر پایه/۲) + (تمبر پایه/۴) = ۱.۷۵ × تمبر پایه
      // مرکز وکلا: تمبر پایه + (تمبر پایه/۲) = ۱.۵ × تمبر پایه
      const stampBaseFirst = stageFirst * 0.05;
      const stampBaseAppeal = stageAppeal * 0.05;
      const kanoonStampFirst =
        stampBaseFirst + stampBaseFirst / 2 + stampBaseFirst / 4;
      const kanoonStampAppeal =
        stampBaseAppeal + stampBaseAppeal / 2 + stampBaseAppeal / 4;
      const markazStampFirst = stampBaseFirst + stampBaseFirst / 2;
      const markazStampAppeal = stampBaseAppeal + stampBaseAppeal / 2;

      return {
        base,
        withMods,
        stageFirst,
        stageAppeal,
        total,
        kanoonStampFirst,
        kanoonStampAppeal,
        markazStampFirst,
        markazStampAppeal,
        hasStages: true,
        isFinancialStamp: true,
        note:
          financialBasis === "agreed"
            ? "مبنای محاسبه، مبلغ حق‌الوکاله مندرج در وکالت‌نامه است."
            : "محاسبه به‌صورت پلکانی طبق نرخ‌های ۸٪/۷٪/۵٪/۴٪/۳٪ انجام شده است.",
      };
    }

    if (caseType === "nonFinancial") {
      const min = 300_000;
      const max = 4_000_000;
      const normalized = Math.min(modifierFraction / 0.55, 1);
      const estimated = min + (max - min) * normalized;
      const stageFirst = estimated * 0.6;
      const stageAppeal = estimated * 0.4;
      return {
        min,
        max,
        base: estimated,
        withMods: estimated,
        stageFirst,
        stageAppeal,
        total: estimated,
        stampTax: estimated * 0.05,
        hasStages: true,
        isRange: true,
        note: "مبلغ دقیق در این دسته در محدوده قانونی، به تشخیص دادگاه تعیین می‌شود؛ عدد نمایش‌داده‌شده یک برآورد بر پایه ضرایب واردشده است. توجه: این محدوده (۳۰۰هزار تا ۴میلیون ریال) از متن قدیمی آیین‌نامه ۱۳۸۵ است و بر خلاف بخش دعاوی مالی، رقم به‌روزشده آن در دسترس نبود؛ پیشنهاد می‌شود عدد نهایی را با کانون یا دادگاه تطبیق دهید.",
      };
    }

    if (caseType === "criminal") {
      if (criminalTier === "other") {
        const min = 300_000;
        const max = 4_000_000;
        const normalized = Math.min(modifierFraction / 0.55, 1);
        const estimated = min + (max - min) * normalized;
        return {
          min,
          max,
          base: estimated,
          withMods: estimated,
          total: estimated,
          stampTax: estimated * 0.05,
          hasStages: false,
          isRange: true,
          note: "سایر دعاوی غیرمالی کیفری؛ مبلغ دقیق به تشخیص دادگاه در همین محدوده تعیین می‌شود. این محدوده از متن قدیمی آیین‌نامه ۱۳۸۵ است؛ رقم به‌روزشده آن در دسترس نبود.",
        };
      }
      const maxFee = criminalTier === "minor" ? 5_000_000 : 10_000_000;
      const perCount = criminalTier === "minor" ? 500_000 : 1_000_000;
      const capExtra = criminalTier === "minor" ? 1_500_000 : 3_000_000;
      const extra = Math.min(Math.min(repeatCounts, 3) * perCount, capExtra);
      const total = maxFee + extra;
      return {
        base: maxFee,
        extra,
        withMods: total,
        total,
        stampTax: total * 0.05,
        hasStages: false,
        note:
          (criminalTier === "minor"
            ? "جرایم دارای مجازات شلاق، جزای نقدی، سرقت حدی، قصاص/دیه عضو یا حبس کمتر از ۱۰ سال."
            : "جرایم مستوجب قصاص نفس، محاربه، حدود مستوجب قتل، دیه نفس یا حبس ۱۰ سال و بیشتر.") +
          " این سقف‌ها از متن قدیمی آیین‌نامه ۱۳۸۵ است؛ رقم به‌روزشده آن در دسترس نبود.",
      };
    }

    if (caseType === "execution") {
      const amt = toRial(executionAmount, unit);
      const total = Math.min(amt * 0.02, 5_000_000);
      const stampTax = total * 0.05;
      return {
        base: total,
        withMods: total,
        total,
        stampTax,
        breakdownFirst: stampBreakdown(stampTax, association),
        hasStages: false,
        note: "حداکثر ۲٪ نسبت به محکوم‌به یا مورد اجرا، با سقف مطلق ۵,۰۰۰,۰۰۰ ریال.",
      };
    }

    if (caseType === "specialized") {
      const amt = toRial(specializedAmount, unit);
      const notional = specializedBodyNotionalFee(amt);
      return {
        base: notional,
        withMods: notional,
        total: notional,
        stampTax: notional * 0.05,
        hasStages: false,
        isSpecialized: true,
        note: "این مبلغ صرفاً برای تعیین تمبر مالیاتی، طبق بند د ماده ۱۰۳ ق.م.م. محاسبه شده و حق‌الوکاله واقعی وکیل، تابع قرارداد فیمابین است.",
      };
    }

    return null;
  }, [
    caseType,
    financialBasis,
    claimAmount,
    agreedFee,
    termination,
    modifierFraction,
    criminalTier,
    repeatCounts,
    executionAmount,
    specializedAmount,
    unit,
    association,
  ]);

  return {
    caseType,
    setCaseType,
    unit,
    setUnit,
    financialBasis,
    setFinancialBasis,
    claimAmount,
    setClaimAmount,
    agreedFee,
    setAgreedFee,
    termination,
    setTermination,
    education,
    setEducation,
    experience,
    setExperience,
    cityTier,
    setCityTier,
    criminalTier,
    setCriminalTier,
    repeatCounts,
    setRepeatCounts,
    association,
    setAssociation,
    executionAmount,
    setExecutionAmount,
    specializedAmount,
    setSpecializedAmount,
    unitLabel,
    result,
    toDisplay: (rial) => toDisplay(rial, unit),
  };
}
